// Display helpers shared by the market grid, the car page and the admin tables.
// Every one of them is null-safe: a missing column must render as a dash, never
// as "undefined" or "NaN".

const CURRENCY_SYMBOLS = {USD: "$", EUR: "€", GEL: "₾"};

export const EMPTY = "—";

/** True when a value carries no information worth rendering. */
export function isBlank(value) {
    return value === undefined || value === null || value === "" ||
           value === "null" || value === "undefined" || value === "NaN";
}

/** Safe text for any field coming from the database. */
export function display(value, fallback = EMPTY) {
    if (isBlank(value)) return fallback;
    return String(value);
}

export function numberWithCommas(value) {
    if (isBlank(value)) return "";
    const numeric = Number(String(value).replace(/[^\d.-]/g, ""));
    if (!Number.isFinite(numeric)) return "";
    return numeric.toLocaleString("en-US");
}

export function formatPrice(value, currency = "USD") {
    if (isBlank(value)) return EMPTY;
    const numeric = Number(String(value).replace(/[^\d.-]/g, ""));
    if (!Number.isFinite(numeric)) return EMPTY;
    const symbol = CURRENCY_SYMBOLS[String(currency || "USD").toUpperCase()] || "";
    // Amounts are shown rounded to a whole number with no decimals, e.g.
    // 33543.32 -> "33,543". Applies to every currency, site-wide.
    const rounded = Math.round(numeric).toLocaleString("en-US");
    return `${symbol}${rounded}`;
}

/** A GEL amount → whole USD using a GEL-per-USD rate (NBG). Null when not computable. */
export function gelToUsd(gel, rate) {
    const r = Number(rate);
    const n = Number(gel);
    if (isBlank(gel) || !Number.isFinite(n) || !Number.isFinite(r) || r <= 0) return null;
    return Math.round(n / r);
}

/** "$5,000" from a GEL amount + rate (rounded whole, no decimals), or null. */
export function formatUsdFromGel(gel, rate) {
    const usd = gelToUsd(gel, rate);
    return usd == null ? null : `$${usd.toLocaleString("en-US")}`;
}

export function formatMileage(value, suffix = "km") {
    const formatted = numberWithCommas(value);
    if (formatted === "") return EMPTY;
    return `${formatted} ${suffix}`;
}

export function formatEngine(value, suffix = "L") {
    if (isBlank(value)) return EMPTY;
    const numeric = Number(String(value).replace(/[^\d.]/g, ""));
    if (!Number.isFinite(numeric) || numeric === 0) return display(value);
    return `${numeric.toFixed(1)} ${suffix}`;
}

/** YYYY-MM-DD / ISO / Date -> DD.MM.YYYY */
export function formatDate(value) {
    if (isBlank(value)) return EMPTY;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return EMPTY;
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`;
}

/**
 * datetime-local <input> value (a local wall-clock, no timezone) -> a precise
 * UTC ISO instant for the API, so the server stores the real moment and it is
 * not shifted by the server's timezone. Returns undefined for empty/invalid.
 */
export function localInputToUtc(value) {
    if (isBlank(value)) return undefined;
    const date = new Date(value); // a bare "YYYY-MM-DDTHH:mm" is parsed as local time
    return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
}

/** A UTC/ISO instant from the API -> datetime-local <input> value (local time). */
export function utcToLocalInput(value) {
    if (isBlank(value)) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function formatDateTime(value) {
    if (isBlank(value)) return EMPTY;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return EMPTY;
    const pad = (n) => String(n).padStart(2, "0");
    return `${formatDate(value)} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

// Month names per language. Russian and Armenian use the genitive case, which
// is the form that reads correctly in a "<day> <month>" date ("3 ноября").
const MONTHS = {
    geo: ["იანვარი", "თებერვალი", "მარტი", "აპრილი", "მაისი", "ივნისი",
          "ივლისი", "აგვისტო", "სექტემბერი", "ოქტომბერი", "ნოემბერი", "დეკემბერი"],
    en: ["January", "February", "March", "April", "May", "June",
         "July", "August", "September", "October", "November", "December"],
    ru: ["января", "февраля", "марта", "апреля", "мая", "июня",
         "июля", "августа", "сентября", "октября", "ноября", "декабря"],
    am: ["հունվարի", "փետրվարի", "մարտի", "ապրիլի", "մայիսի", "հունիսի",
         "հուլիսի", "օգոստոսի", "սեպտեմբերի", "հոկտեմբերի", "նոյեմբերի", "դեկտեմբերի"],
};

/** A date rendered with the month name in the given language, e.g. "3 ნოემბერი". */
export function formatDateLong(value, lang = "geo") {
    if (isBlank(value)) return EMPTY;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return EMPTY;
    const months = MONTHS[lang] || MONTHS.geo;
    const day = date.getDate();
    const month = months[date.getMonth()];
    // Armenian reads month-first ("նոյեմբերի 3").
    return lang === "am" ? `${month} ${day}` : `${day} ${month}`;
}

const GEO_DATE_TITLE = new RegExp(`^\\s*(\\d{1,2})\\s+(${MONTHS.geo.join("|")})\\s*$`);

/**
 * Auction events are usually titled with their date, typed in Georgian
 * ("3 ნოემბერი"). When the title is exactly such a date, re-render it in the
 * active language (keeping the day number); any other title is left untouched.
 */
export function localizeEventTitle(title, lang = "geo") {
    if (isBlank(title)) return "";
    if (lang === "geo") return String(title);
    const match = GEO_DATE_TITLE.exec(String(title));
    if (!match) return String(title);
    const monthIndex = MONTHS.geo.indexOf(match[2]);
    if (monthIndex < 0) return String(title);
    const day = match[1];
    const month = (MONTHS[lang] || MONTHS.geo)[monthIndex];
    return lang === "am" ? `${month} ${day}` : `${day} ${month}`;
}

/** "BMW X5 2019", skipping whatever is missing. */
export function carTitle(car) {
    if (!car) return EMPTY;
    const parts = [car.mark, car.model, car.year].filter((part) => !isBlank(part));
    return parts.length > 0 ? parts.join(" ") : EMPTY;
}

/**
 * The API always returns an array, but older cached responses (and the share
 * page) may still hand over a JSON string.
 */
export function toImageArray(images) {
    if (Array.isArray(images)) return images.filter((img) => typeof img === "string" && img !== "");
    if (typeof images === "string" && images !== "" && images !== "null") {
        try {
            const parsed = JSON.parse(images);
            return Array.isArray(parsed) ? parsed.filter((img) => typeof img === "string" && img !== "") : [];
        } catch (e) {
            return [];
        }
    }
    return [];
}

export function coverImage(car) {
    const images = toImageArray(car && car.images);
    return images.length > 0 ? images[0] : null;
}

/** Builds a query string, dropping every empty value so no "&mark=" is sent. */
export function toQueryString(params) {
    const search = new URLSearchParams();
    Object.entries(params || {}).forEach(([key, value]) => {
        if (isBlank(value)) return;
        search.set(key, String(value));
    });
    return search.toString();
}

// -----------------------------------------------------------------------------
// Spec value translation
//
// The technical spec fields are fixed drop-downs stored in a canonical form
// (English for fuel/transmission/drive/category, Georgian for the common
// colours). Those values reach the public pages verbatim, so without this they
// stay in their stored language whatever the site locale is. Each entry maps a
// stored value to [geo, en, ru, am]; an unknown value is shown as typed.
// -----------------------------------------------------------------------------
const LANG_INDEX = { geo: 0, en: 1, ru: 2, am: 3 };

const SPEC_VALUES = {
    fuel: {
        gasoline: ["ბენზინი", "Gasoline", "Бензин", "Բենզին"],
        diesel: ["დიზელი", "Diesel", "Дизель", "Դիզել"],
        hybrid: ["ჰიბრიდი", "Hybrid", "Гибрид", "Հիբրիդ"],
        electric: ["ელექტრო", "Electric", "Электро", "Էլեկտրական"],
        gas: ["გაზი", "Gas", "Газ", "Գազ"],
    },
    transmission: {
        automatic: ["ავტომატიკა", "Automatic", "Автомат", "Ավտոմատ"],
        manual: ["მექანიკა", "Manual", "Механика", "Մեխանիկական"],
        cvt: ["ვარიატორი", "CVT", "Вариатор", "Վարիատոր"],
        tiptronic: ["ტიპტრონიკი", "Tiptronic", "Типтроник", "Տիպտրոնիկ"],
    },
    drive: {
        "front wheels drive": ["წინა წამყვანი", "Front-wheel drive", "Передний привод", "Առջևի քարշ"],
        "rear wheels drive": ["უკანა წამყვანი", "Rear-wheel drive", "Задний привод", "Հետևի քարշ"],
        "all wheels drive": ["სრული წამყვანი", "All-wheel drive", "Полный привод", "Լրիվ քարշ"],
    },
    category: {
        sedan: ["სედანი", "Sedan", "Седан", "Սեդան"],
        jeep: ["ჯიპი", "Jeep", "Внедорожник", "Ջիպ"],
        coupe: ["კუპე", "Coupe", "Купе", "Կուպե"],
        hatchback: ["ჰეჩბეკი", "Hatchback", "Хэтчбек", "Հեթչբեք"],
        minivan: ["მინივენი", "Minivan", "Минивэн", "Մինիվեն"],
        "micro-bus": ["მიკროავტობუსი", "Micro-bus", "Микроавтобус", "Միկրոավտոբուս"],
        pickup: ["პიკაპი", "Pickup", "Пикап", "Պիկապ"],
        universal: ["უნივერსალი", "Wagon", "Универсал", "Ունիվերսալ"],
        motorcycle: ["მოტოციკლი", "Motorcycle", "Мотоцикл", "Մոտոցիկլ"],
        quadrocycle: ["კვადროციკლი", "Quad bike", "Квадроцикл", "Քվադրոցիկլ"],
    },
    color: {
        "შავი": ["შავი", "Black", "Чёрный", "Սև"],
        "თეთრი": ["თეთრი", "White", "Белый", "Սպիտակ"],
        "ვერცხლისფერი": ["ვერცხლისფერი", "Silver", "Серебристый", "Արծաթագույն"],
        "ნაცრისფერი": ["ნაცრისფერი", "Gray", "Серый", "Մոխրագույն"],
        "რუხი": ["რუხი", "Gray", "Серый", "Մոխրագույն"],
        "წითელი": ["წითელი", "Red", "Красный", "Կարմիր"],
        "ლურჯი": ["ლურჯი", "Blue", "Синий", "Կապույտ"],
        "ცისფერი": ["ცისფერი", "Light blue", "Голубой", "Երկնագույն"],
        "მწვანე": ["მწვანე", "Green", "Зелёный", "Կանաչ"],
        "ყვითელი": ["ყვითელი", "Yellow", "Жёлтый", "Դեղին"],
        "ყავისფერი": ["ყავისფერი", "Brown", "Коричневый", "Շագանակագույն"],
        "ოქროსფერი": ["ოქროსფერი", "Gold", "Золотистый", "Ոսկեգույն"],
        "ბეჟი": ["ბეჟი", "Beige", "Бежевый", "Բեժ"],
        "ნარინჯისფერი": ["ნარინჯისფერი", "Orange", "Оранжевый", "Նարնջագույն"],
        "იისფერი": ["იისფერი", "Purple", "Фиолетовый", "Մանուշակագույն"],
        "იასამნისფერი": ["იასამნისფერი", "Purple", "Фиолетовый", "Մանուշակագույն"],
        "ბორდო": ["ბორდო", "Burgundy", "Бордовый", "Բորդո"],
    },
    // Car locations are admin-typed free text, but in practice they are a short
    // fixed set of places. The known ones are translated; anything else (a new
    // place name) falls through unchanged.
    location: {
        "რუსთავის ავტო ბაზრობა": ["რუსთავის ავტო ბაზრობა", "Rustavi Auto Market", "Авторынок Рустави", "Ռուսթավիի ավտոշուկա"],
        "რუსთავის ავტობაზრობა": ["რუსთავის ავტობაზრობა", "Rustavi Auto Market", "Авторынок Рустави", "Ռուսթավիի ավտոշուկա"],
        "თბილისი": ["თბილისი", "Tbilisi", "Тбилиси", "Թբիլիսի"],
        "რუსთავი": ["რუსთავი", "Rustavi", "Рустави", "Ռուսթավի"],
    },
};

/**
 * Translate a stored spec value into the active language, e.g.
 * translateSpec("fuel", "Gasoline", "ru") -> "Бензин". Unknown values (free
 * text such as a place name) are returned unchanged.
 */
export function translateSpec(kind, value, lang = "geo") {
    if (isBlank(value)) return value;
    const table = SPEC_VALUES[kind];
    if (!table) return value;
    const row = table[String(value).trim().toLowerCase()] || table[String(value).trim()];
    if (!row) return value;
    return row[LANG_INDEX[lang] ?? 0] || value;
}
